import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import type { BillingMode } from './types';

/**
 * One-stop hook for the platform-wide billing-mode flags + credit-pricing config.
 * Backed by GET /platform/billing-mode (public, no auth) — same call regardless of
 * whether you're on the marketing pricing page, the in-app subscription page, or
 * the addon slider confirmation modal.
 *
 * Returned object is always safe to destructure — when the request hasn't completed
 * yet, the defaults skew toward LEGACY behaviour (wallet OFF, all zero costs) so a
 * brief loading window doesn't accidentally render wallet copy to a marketing
 * visitor before we know it's actually enabled.
 */
/**
 * Fully-shaped fallback. Also the floor every field is merged over, so a payload from an OLDER
 * deployed API that predates a field (e.g. `trial`, added later) can't leave a hole that crashes a
 * consumer. This is not theoretical: the SSG build prefetches the LIVE prod API, so during a deploy
 * the NEW web bundle renders against the CURRENTLY-running (older) API's payload — a missing field
 * there previously threw `Cannot read properties of undefined` in the homepage prerender and
 * deadlocked the deploy (the build depended on prod already having the field it was shipping).
 */
const BILLING_MODE_DEFAULTS: BillingMode = {
  chargeUpgradesImmediately: true,
  unifiedWalletEnabled: false,
  creditCosts: { detection: 0, emailVerification: 0, blacklist: 0, inboxPlacement: 0, phoneValidation: 0, domainHealth: 0 },
  referenceSaleCentsPer1000: 0,
  trial: { days: 3, credits: 500 },
};

export function useBillingMode(): BillingMode {
  const { data } = useQuery({
    queryKey: ['billing-mode'],
    // `auth: false` so the public marketing pricing page (unauthenticated) can call it.
    // The endpoint is @Public() server-side; this just stops the api helper from
    // attaching a missing JWT.
    queryFn: () => api<BillingMode>('/platform/billing-mode', { auth: false }),
    staleTime: 60_000,
  });
  if (!data) return BILLING_MODE_DEFAULTS;
  // Merge over the defaults so EVERY field is present even if the API payload is an older shape —
  // never return `data` raw. `trial` + `creditCosts` are merged explicitly because a shallow spread
  // wouldn't backfill a missing nested object / missing sub-keys.
  return {
    ...BILLING_MODE_DEFAULTS,
    ...data,
    creditCosts: { ...BILLING_MODE_DEFAULTS.creditCosts, ...(data.creditCosts ?? {}) },
    trial: { ...BILLING_MODE_DEFAULTS.trial, ...(data.trial ?? {}) },
  };
}
